import { useEffect, useState } from "react";
import { Ticker } from "./Ticker";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, fetchProductsCached } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { LogoMark } from "./Logo";

export function Footer() {
  const { t, catName } = useLang();
  const [subEmail, setSubEmail] = useState("");
  const [cats, setCats] = useState([]);

  useEffect(() => {
    // categories follow the actual catalog — new categories appear as products are added
    fetchProductsCached().then((list) => {
      setCats([...new Set(list.map((p) => p.category).filter(Boolean))]);
    }).catch(() => {});
  }, []);

  const subscribe = async () => {
    if (!subEmail.trim()) return;
    try {
      await api.post("/subscribe", { email: subEmail });
      toast.success(t("subscribed"));
      setSubEmail("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Error"));
    }
  };

  const linkCls = "font-badge text-sm transition-colors duration-150 hover:opacity-100";
  const muted = { color: "var(--zjz-muted)" };
  const cell = { borderColor: "var(--zjz-border)" };

  return (
    <footer
      data-testid="site-footer"
      className="border-t"
      style={{ background: "var(--zjz-bg)", borderColor: "var(--zjz-border)", color: "var(--zjz-text)" }}
    >
      <div className="py-3 overflow-hidden" style={{ background: "var(--zjz-surface)", borderBottom: "1px solid var(--zjz-border)" }}>
        <Ticker duration={32} testid="footer-ticker">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="font-badge text-sm mx-6 whitespace-nowrap" style={muted}>
              ZEJZEN · CURATED · CHECKED · MEASURED · ONE OF ONE · FROM GERMANY ·
            </span>
          ))}
        </Ticker>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4">
        <div className="p-8 md:border-r" style={cell}>
          <p className="font-['Space_Grotesk'] text-2xl font-extrabold tracking-[0.18em] uppercase flex items-center gap-2.5">
            <LogoMark className="w-7 h-5" />
            Zejzen
          </p>
          <p className="font-badge text-[10px] mt-3" style={muted}>{t("footerTag")}</p>
          <p className="font-badge text-[10px] mt-1" style={muted}>info@zejzen.com</p>
        </div>

        <div className="p-8 md:border-r border-t md:border-t-0" style={cell}>
          <p className="font-badge text-[10px] mb-3" style={muted}>{t("categories")}</p>
          {/* min-height reserves the space before the category list arrives — prevents footer CLS */}
          <div className="flex flex-col gap-1.5 min-h-[104px]">
            {cats.map((c) => (
              <Link
                key={c}
                to={`/shop?cat=${c}`}
                data-testid={`footer-category-${c}`}
                className={linkCls}
                style={{ color: "var(--zjz-text)" }}
              >
                {catName(c)}
              </Link>
            ))}
          </div>
        </div>

        <div className="p-8 border-t md:border-t-0" style={cell}>
          <p className="font-badge text-[10px] mb-3" style={muted}>{t("dropAlerts")}</p>
          <div className="flex">
            <input
              data-testid="subscribe-email-input"
              aria-label="E-Mail für Benachrichtigungen"
              value={subEmail}
              onChange={(e) => setSubEmail(e.target.value)}
              placeholder={t("subscribePh")}
              className="flex-1 min-w-0 border border-r-0 px-3 py-2 text-sm focus:outline-none"
              style={{ background: "var(--zjz-surface)", borderColor: "var(--zjz-border)", color: "var(--zjz-text)" }}
            />
            <button
              onClick={subscribe}
              data-testid="subscribe-submit-button"
              className="font-badge text-[10px] px-4 py-2 whitespace-nowrap transition-opacity hover:opacity-85"
              style={{ background: "var(--zjz-accent)", color: "#fff" }}
            >
              {t("subscribeBtn")}
            </button>
          </div>
          <p className="font-badge text-xs mt-4">zejzen.com</p>
          <p className="font-badge text-[10px] mt-2" style={muted}>{t("secure")}</p>
        </div>

        <div className="p-8 border-t md:border-t-0 md:border-l" style={cell}>
          <p className="font-badge text-[10px] mb-3" style={muted}>{t("legalTitle")}</p>
          <div className="flex flex-col gap-1.5">
            <Link to="/track" data-testid="footer-track-link" className={linkCls} style={{ color: "var(--zjz-text)" }}>{t("trackLink")}</Link>
            <Link to="/impressum" data-testid="footer-legal-impressum" className={linkCls} style={{ color: "var(--zjz-text)" }}>Impressum</Link>
            <Link to="/datenschutz" data-testid="footer-legal-datenschutz" className={linkCls} style={{ color: "var(--zjz-text)" }}>Datenschutz</Link>
            <Link to="/widerruf" data-testid="footer-legal-widerruf" className={linkCls} style={{ color: "var(--zjz-text)" }}>Widerrufsrecht</Link>
            <Link to="/versand" data-testid="footer-legal-versand" className={linkCls} style={{ color: "var(--zjz-text)" }}>Versand &amp; Lieferung</Link>
            <Link to="/agb" data-testid="footer-legal-agb" className={linkCls} style={{ color: "var(--zjz-text)" }}>AGB</Link>
            <Link to="/zahlung" data-testid="footer-payment-link" className={linkCls} style={{ color: "var(--zjz-text)" }}>{t("paymentMethods")}</Link>
            <Link to="/kontakt" data-testid="footer-contact-link" className={linkCls} style={{ color: "var(--zjz-text)" }}>{t("contactTitle")}</Link>
          </div>
        </div>
      </div>

      <div className="border-t px-4 sm:px-8 py-3 flex flex-wrap justify-between items-center gap-2" style={cell}>
        <span className="font-badge text-[10px]" style={muted} data-testid="footer-copyright">
          © {new Date().getFullYear()} ZEJZEN · {t("basedIn").toUpperCase()}
        </span>
        <div className="flex items-center gap-4">
          <button
            onClick={() => window.dispatchEvent(new Event("zejzen:show-cookies"))}
            data-testid="footer-cookie-button"
            className="font-badge text-[10px] transition-colors duration-150"
            style={muted}
          >
            {t("cookieSettings")}
          </button>
          <span className="font-badge text-[10px]" style={muted}>ONE OF ONE</span>
        </div>
      </div>
    </footer>
  );
}
